import { isNative } from "./native.js";

/** Shared files are written here first (the app's cache), and removed on sign-out. */
const SHARE_DIR = "share";

function base64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Saves a file the user asked for: a download in the browser, the Android share sheet in
 * the app (spec08 FR16), so nothing lands in shared storage without the user picking where.
 */
export async function saveFile(blob: Blob, name: string) {
  if (!isNative) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  const [{ Directory, Filesystem }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const { uri } = await Filesystem.writeFile({
    path: `${SHARE_DIR}/${name.replace(/[\\/:*?"<>|]+/g, "-") || "file"}`,
    data: await base64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  try {
    await Share.share({ files: [uri], dialogTitle: name });
  } catch {
    // Closing the share sheet without picking a target rejects; nothing to do.
  }
}

/** spec08 FR8: sign-out removes files that were shared from the app. */
export async function clearSharedFiles() {
  if (!isNative) return;
  const { Directory, Filesystem } = await import("@capacitor/filesystem");
  await Filesystem.rmdir({ path: SHARE_DIR, directory: Directory.Cache, recursive: true }).catch(
    () => undefined,
  );
}
